import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { environment } from '@environments/environment';
import { IMenuItem } from '@app/shared/interfaces/menu.interface';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-side-menu',
    imports: [CommonModule, RouterModule],
    templateUrl: './side-menu.component.html',
    styleUrl: './side-menu.component.scss'
})
export class SideMenuComponent implements OnInit {
    private router = inject(Router);

    items: IMenuItem[] = [];
    logoPath = environment.sideMenuLogo;
    logoText = environment.companyName;
    isMobileMenuOpen = signal(false);

    ngOnInit(): void {
        this.initMenuItems();
        this.expandActiveMenu();

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.collapseAllMenus();
            this.expandActiveMenu();
        });
    }

    toggleSubmenu(item: IMenuItem): void {
        if (item.children && item.children.length > 0) {
            const isExpanding = !item.expanded;
            this.collapseAllMenus();
            if (isExpanding) {
                item.expanded = true;
            }
        }
    }

    toggleMobileMenu(): void {
        this.isMobileMenuOpen.update((v) => !v);
    }

    closeMobileMenu(): void {
        this.isMobileMenuOpen.set(false);
    }

    isActiveRoute(route: string | undefined): boolean {
        if (!route) return false;
        return this.router.url === route;
    }

    hasActiveChild(item: IMenuItem): boolean {
        if (!item.children) return false;
        return item.children.some((child) => this.isActiveRoute(child.route));
    }

    private initMenuItems(): void {
        this.items = [
            {
                label: 'Dashboard',
                icon: 'dashboard',
                route: '/'
            },
            {
                label: 'Financial Reports',
                icon: 'assessment',
                children: [
                    { label: 'Revenue Comp. Detailed', route: '/reports/accounting-report' },
                    { label: 'Revenue Comp. General', route: '/reports/yearly-revenue-report' }
                ],
                expanded: false,
                hide: !environment.showReports
            },
            {
                label: 'Flights',
                icon: 'flight',
                route: '/flights'
            },
            {
                label: 'User & Access',
                icon: 'manage_accounts',
                children: [
                    { label: 'Users Management', route: '/definitions/user-definitions' },
                    { label: 'Airline Rules Management', route: '/definitions/airline-rules-definitions' },
                    { label: 'APIS Rules Management', route: '/parameters/apis-definitions-management' }
                ],
                expanded: false
            },
            {
                label: 'Content',
                icon: 'auto_stories',
                route: '/cms',
                hide: !environment.showLandingPage
            },
            {
                label: 'Products & Ancillaries',
                icon: 'settings_input_component',
                children: [
                    { label: 'Product Management', route: '/parameters/product-management' },
                    { label: 'Product Pricing Management', route: '/parameters/additional-cost-management' },
                    { label: 'Fare bundles', route: '/parameters/ancillary-management' },
                    { label: 'Fare bundles Markups', route: '/parameters/ancillary-markup-management' },
                    { label: 'Coupons', route: '/parameters/discount-coupon', hide: !environment.showDiscountCoupon }
                ],
                expanded: false,
                hide: !environment.showParameters
            },
            {
                label: 'Payment Definitions',
                icon: 'credit_card',
                children: [
                    { label: 'Tenant Definition', route: '/payment-definitions/tenant-definition' },
                    { label: 'Tenant User Definition', route: '/payment-definitions/tenant-user-definition' },
                    { label: 'PSP Definition', route: '/payment-definitions/psp-definition' },
                    { label: 'PSP Currency', route: '/payment-definitions/psp-currency' },
                    { label: 'PSP Setting', route: '/payment-definitions/psp-setting' },
                    { label: 'PSP Error Code', route: '/payment-definitions/psp-error-code' },
                    { label: 'Payment Transaction Log', route: '/payment-definitions/payment-transaction-log' }
                ],
                expanded: false
            }
        ];
    }

    private expandActiveMenu(): void {
        const currentUrl = this.router.url;
        this.items.forEach((item) => {
            if (item.children) {
                const hasActiveChild = item.children.some((child) => child.route === currentUrl);
                if (hasActiveChild) {
                    item.expanded = true;
                }
            }
        });
    }

    private collapseAllMenus(): void {
        this.items.forEach((item) => {
            item.expanded = false;
        });
    }
}
